import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import FlipMove from 'react-flip-move';
import { useErrorMessages } from '../../../aida/hooks';
import { useUserSession } from '../../../models/hooks/appStateHooks';
import { useEventLogging } from '../../../models/Logging';
import {
	getFeedItemViewModel,
	invalidateDashboardFeed,
	useDashboardFeed,
	useSuppressDashboardItem,
} from '../../../queries';
import {
	AppDrawer,
	AppDrawerToolbar,
	AppDrawerToolbarButtonAiAssistant,
	StickyAppDrawerContent,
} from '../../components-v2/Drawer/AppDrawer';
import { Page } from '../../components-v2/PageLayout';
import { AppBarNavigationItemPortalDestinationId } from '../../components/AppBar';
import { CampaignCard } from '../../components/cards/CampaignCard';
import { EmailActivityCard } from '../../components/cards/EmailActivityCard';
import { EmptyState } from '../../components/EmptyState';
import { LoadingSpinner } from '../../components/LoadingSpinner';
import { NavigationBreadcrumbsBar } from '../../components/NavigationBreadcrumbsBar';
import { Portal } from '../../components/Portal';
import { EmailInCircleGraphic } from '../../components/svgs/graphics/EmailInCircleGraphic';
import { DefaultUniversalAssistantChat } from '../templates/AssistantChat';
import { styleSheet } from './styles';

const PAGE_SIZE = 100;
const FEED_TYPE = 'EmailActivity';

export function RecentlyViewedEmails() {
	const { logApiError } = useEventLogging('RecentlyViewedEmails');
	const errorMessages = useErrorMessages();
	const userSession = useUserSession();
	const dashboardFeed = useDashboardFeed({ typeOf: FEED_TYPE, pageSize: PAGE_SIZE });
	const suppressMutation = useSuppressDashboardItem({
		onSuccess: () => {
			invalidateDashboardFeed({ typeOf: FEED_TYPE, pageSize: PAGE_SIZE });
		},
		onError: error => {
			errorMessages.push(error);

			logApiError('SuppressRecentMeeting-Error', error);
		},
	});
	const onSuppressClicked = (item: Api.EmailActivityViewModel | Api.CampaignViewModel) => {
		let suppressionContext: Api.DashboardSuppressContext;
		if (item instanceof Api.CampaignViewModel) {
			suppressionContext = Api.DashboardSuppressContext.RecentBulkEmails;
		} else if (item instanceof Api.EmailActivityViewModel) {
			suppressionContext = Api.DashboardSuppressContext.EmailActivity;
		}

		if (!suppressionContext) {
			return;
		}
		suppressMutation.mutate({
			id: item.id,
			context: suppressionContext,
		});
	};
	const feedItems = dashboardFeed.data?.values ?? [];
	return (
		<Page styles={[styleSheet.container]}>
			<Portal destination={AppBarNavigationItemPortalDestinationId}>
				<NavigationBreadcrumbsBar currentLocationName='Recently Viewed Emails' />
			</Portal>

			{dashboardFeed.isPending ? <LoadingSpinner type='large' /> : null}
			{dashboardFeed.isSuccess && feedItems.length === 0 ? (
				<EmptyState
					image={<EmailInCircleGraphic />}
					text={"You don't have any recently viewed emails"}
					subtext='More emails will appear on your dashboard as they are viewed.'
				/>
			) : null}
			{dashboardFeed.isSuccess && feedItems.length > 0 ? (
				<FlipMove
					enterAnimation={true}
					leaveAnimation={true}
					staggerDelayBy={50}
					staggerDurationBy={10}
					appearAnimation={false}
				>
					{feedItems.map(item => {
						const x = getFeedItemViewModel({ feedItem: item, userSession });
						if (x instanceof Api.EmailActivityViewModel) {
							return (
								<EmailActivityCard
									className={css(styleSheet.item)}
									emailActivity={x}
									key={x.id}
									onSuppressClicked={() => onSuppressClicked(x)}
								/>
							);
						} else if (x instanceof Api.CampaignViewModel) {
							return (
								<CampaignCard
									className={css(styleSheet.item)}
									campaign={x}
									key={x.id}
									onSuppressClicked={() => onSuppressClicked(x)}
								/>
							);
						}
					})}
				</FlipMove>
			) : null}
			<AppDrawer>
				<StickyAppDrawerContent value='ai-assistant'>
					<DefaultUniversalAssistantChat />
				</StickyAppDrawerContent>
				<AppDrawerToolbar>
					<AppDrawerToolbarButtonAiAssistant value='ai-assistant' />
				</AppDrawerToolbar>
			</AppDrawer>
		</Page>
	);
}
